import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, MapPin, Phone, Mail, Clock } from "lucide-react";
import { BUSINESS, CITIES } from "@/constants/business";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#faf7f2] border-t border-[#e5ddd0] pt-24 pb-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16">
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img
                src="/brand/vila-logo-dark.png"
                alt="Vila Elevators & Escalators"
                className="h-11 w-auto"
              />
              <span className="text-[10px] text-[#6b6b6b] uppercase tracking-[0.2em] font-medium border-l border-[#e5ddd0] pl-3">
                Elevators &<br />Escalators
              </span>
            </Link>
            <p className="text-[#6b6b6b] text-sm leading-relaxed mb-6">
              South India's trusted manufacturer of elevators, escalators, home lifts and moving walkways. In-house CNC precision engineering from our Bommasandra facility.
            </p>
            <div className="flex gap-3">
              <a href={BUSINESS.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="w-10 h-10 border border-[#e5ddd0] hover:border-[#b08d57] hover:text-[#b08d57] flex items-center justify-center text-[#6b6b6b] transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={BUSINESS.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="w-10 h-10 border border-[#e5ddd0] hover:border-[#b08d57] hover:text-[#b08d57] flex items-center justify-center text-[#6b6b6b] transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={BUSINESS.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin" className="w-10 h-10 border border-[#e5ddd0] hover:border-[#b08d57] hover:text-[#b08d57] flex items-center justify-center text-[#6b6b6b] transition-colors">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href={BUSINESS.social.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="footer-youtube" className="w-10 h-10 border border-[#e5ddd0] hover:border-[#b08d57] hover:text-[#b08d57] flex items-center justify-center text-[#6b6b6b] transition-colors">
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-5">Explore</div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/" className="text-[#6b6b6b] hover:text-[#1a1a1a] transition-colors">Home</Link></li>
              <li><Link to="/services" className="text-[#6b6b6b] hover:text-[#1a1a1a] transition-colors">Services</Link></li>
              <li><Link to="/products" className="text-[#6b6b6b] hover:text-[#1a1a1a] transition-colors">Products</Link></li>
              <li><Link to="/manufacturing" className="text-[#6b6b6b] hover:text-[#1a1a1a] transition-colors">Manufacturing</Link></li>
              <li><Link to="/blog" className="text-[#6b6b6b] hover:text-[#1a1a1a] transition-colors">Blog</Link></li>
              <li><Link to="/about" className="text-[#6b6b6b] hover:text-[#1a1a1a] transition-colors">About</Link></li>
              <li><Link to="/careers" data-testid="footer-careers" className="text-[#6b6b6b] hover:text-[#1a1a1a] transition-colors">Careers</Link></li>
              <li><Link to="/contact" className="text-[#6b6b6b] hover:text-[#1a1a1a] transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-5">Service Areas</div>
            <ul className="space-y-3 text-sm">
              {CITIES.map((c) => (
                <li key={c.slug}>
                  <Link to={`/elevators-${c.slug}`} className="text-[#6b6b6b] hover:text-[#1a1a1a] transition-colors">
                    Elevators in {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-5">Reach Us</div>
            <ul className="space-y-4 text-sm text-[#6b6b6b]">
              <li className="flex gap-3">
                <MapPin className="w-4 h-4 text-[#b08d57] flex-shrink-0 mt-1" />
                <span className="leading-relaxed">
                  {BUSINESS.address.line1}, {BUSINESS.address.line2}, {BUSINESS.address.city} – {BUSINESS.address.postal}, {BUSINESS.address.state}, {BUSINESS.address.country}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#b08d57] flex-shrink-0" />
                <a href={`tel:${BUSINESS.phoneRaw}`} className="hover:text-[#1a1a1a] transition-colors">{BUSINESS.phone}</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#b08d57] flex-shrink-0" />
                <a href={`mailto:${BUSINESS.email}`} className="hover:text-[#1a1a1a] transition-colors">{BUSINESS.email}</a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-[#b08d57] flex-shrink-0" />
                <span>{BUSINESS.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-[#e5ddd0] pt-8 pb-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Popular Searches</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#8a8a8a]">
            <Link to="/elevator-company-bangalore" className="hover:text-[#b08d57] transition-colors">Elevator Company Bangalore</Link>
            <Link to="/lift-manufacturers-karnataka" className="hover:text-[#b08d57] transition-colors">Lift Manufacturers Karnataka</Link>
            <Link to="/home-lift-bangalore" className="hover:text-[#b08d57] transition-colors">Home Lift Bangalore</Link>
            <Link to="/elevator-amc-bangalore" className="hover:text-[#b08d57] transition-colors">Elevator AMC Bangalore</Link>
            <Link to="/elevator-installation-bangalore" className="hover:text-[#b08d57] transition-colors">Elevator Installation Bangalore</Link>
            <Link to="/hydraulic-lift-bangalore" className="hover:text-[#b08d57] transition-colors">Hydraulic Lift Bangalore</Link>
          </div>
        </div>

        <div className="border-t border-[#e5ddd0] pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-[#8a8a8a]">
          <div>© {new Date().getFullYear()} Vila Elevators & Escalators. All rights reserved.</div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 justify-center">
            <Link to="/privacy-policy" data-testid="footer-privacy" className="hover:text-[#b08d57] transition-colors">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-[#b08d57] transition-colors">Terms of Service</Link>
            <span className="text-[#8a8a8a]">Manufacturer · Bengaluru · Serving KA, AP, TS, TN</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
